import { Chain } from '@1delta/chain-registry'
import { proxyNativeFetch, type RelayProxyConfig } from '@1delta/proxy-fetch'

/**
 * Back-compat alias. New callers should use `RelayProxyConfig` directly from
 * `@1delta/proxy-fetch`.
 */
export type MorphoSubgraphProxyConfig = RelayProxyConfig

export interface MorphoUserMarketBalance {
  /** 0x-prefixed lowercase 66-char market key */
  marketId: string
  /** Raw supply shares as decimal string */
  supplyShares: string
  /** Raw borrow shares as decimal string */
  borrowShares: string
  /** Raw collateral amount as decimal string */
  collateral: string
}

// ─── Goldsky-style subgraphs (newer/smaller chains) ──────────────────────────

const MORPHO_USER_SUBGRAPH_URLS: Record<string, string> = {
  [Chain.SEI_NETWORK]:
    'https://api.goldsky.com/api/public/project_cmiergfbv4vma01vb642yaeam/subgraphs/morphoblue-sei/1.0.1/gn',
  [Chain.CELO_MAINNET]:
    'https://api.goldsky.com/api/public/project_cmiergfbv4vma01vb642yaeam/subgraphs/morphoblue-celo/1.0.4/gn',
  [Chain.LISK]:
    'https://api.goldsky.com/api/public/project_cmiergfbv4vma01vb642yaeam/subgraphs/morphobluelisk/1.0.1/gn',
  [Chain.SONEIUM]:
    'https://api.goldsky.com/api/public/project_cmiergfbv4vma01vb642yaeam/subgraphs/morphobluesoneium/1.0.2/gn',
  [Chain.TAC_MAINNET]:
    'https://api.goldsky.com/api/public/project_cmiergfbv4vma01vb642yaeam/subgraphs/morphoblue-tac/1.0.0/gn',
  // Hemi uses a different provider, but speaks the same GraphQL schema.
  [Chain.HEMI_NETWORK]: 'https://feather.securesecrets.org/hemi-mopho-blue/',
}

// ─── Morpho Blue API (main chains) ───────────────────────────────────────────

const MORPHO_BLUE_API_URL = 'https://blue-api.morpho.org/graphql'

// Maps chain string ID → numeric chain ID expected by the Morpho Blue API
const MORPHO_API_CHAIN_IDS: Record<string, number> = {
  [Chain.ETHEREUM_MAINNET]: 1,
  [Chain.BASE]: 8453,
  [Chain.ARBITRUM_ONE]: 42161,
  [Chain.OP_MAINNET]: 10,
  [Chain.POLYGON_MAINNET]: 137,
  [Chain.GNOSIS]: 100,
  [Chain.FRAXTAL]: 252,
  [Chain.BLAST]: 81457,
  [Chain.LINEA]: 59144,
  [Chain.TAIKO_ALETHIA]: 167000,
  [Chain.AVALANCHE_C_CHAIN]: 43114,
  [Chain.ZKSYNC_MAINNET]: 324,
  [Chain.SONIC_MAINNET]: 146,
  [Chain.SCROLL]: 534352,
  [Chain.BERACHAIN]: 80094,
  [Chain.HYPEREVM]: 999,
  [Chain.UNICHAIN]: 130,
}

// ─── Raw response types ───────────────────────────────────────────────────────

interface SubgraphPosition {
  market: { id: string }
  side: 'SUPPLIER' | 'BORROWER' | 'COLLATERAL' | string
  balance: string
  shares: string | null
}

interface SubgraphAccountResponse {
  data?: {
    account?: { positions?: SubgraphPosition[] } | null
  }
}

interface ApiMarketPosition {
  market: { uniqueKey: string }
  state: {
    supplyShares: number | string | null
    borrowShares: number | string | null
    collateral: number | string | null
  } | null
}

interface ApiUserResponse {
  data?: {
    userByAddress?: { marketPositions?: ApiMarketPosition[] } | null
  }
}

// ─── Timing + dedup-cache constants ──────────────────────────────────────────

/**
 * Hard upper bound on how long we will wait for the subgraph / Morpho API.
 * When it trips, the caller falls back to querying all markets on-chain — so
 * correctness is preserved, only freshness of the pre-filter is lost.
 */
const FETCH_TIMEOUT_MS = 3_000

/**
 * In-memory micro-cache TTL. Deliberately tiny: a user who just deposited
 * through an integrator MUST see the new position in the next poll. The cache
 * only exists to collapse bursts of concurrent requests onto the same upstream
 * call (dedup) and to absorb sub-second poll loops without hitting upstream
 * rate limits. The upstream indexer lag (seconds → tens of seconds) already
 * dominates this window, so a 2-second TTL adds no meaningful staleness on top
 * of what the data source itself imposes.
 */
const CACHE_TTL_MS = 2_000

// ─── Helpers ──────────────────────────────────────────────────────────────────

const buildSubgraphQuery = (account: string) => `
{
  account(id: "${account.toLowerCase()}") {
    positions {
      market { id }
      side
      balance
      shares
    }
  }
}`

const buildApiQuery = (account: string, chainId: number) => `
{
  userByAddress(address: "${account}", chainId: ${chainId}) {
    marketPositions {
      market { uniqueKey: marketId }
      state {
        supplyShares
        borrowShares
        collateral
      }
    }
  }
}`

function isNonZero(value: string | number | null | undefined): boolean {
  if (value === null || value === undefined) return false
  return value !== '0' && value !== 0
}

async function attemptFetchJson(
  url: string,
  body: string,
  proxyConfig: RelayProxyConfig | undefined,
): Promise<unknown | undefined> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    const response = await proxyNativeFetch(url, proxyConfig, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: controller.signal,
    })
    if (!response.ok) return undefined
    return await response.json()
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Try a direct fetch first; if it fails (timeout, non-2xx, transport error,
 * JSON parse) AND a relay proxy is configured, retry through the proxy. The
 * proxy is purely a safety net for upstream rate-limits / egress-IP blocks —
 * healthy direct calls never pay the proxy hop.
 *
 * Worst-case latency is 2 × FETCH_TIMEOUT_MS. Both attempts feed into the same
 * `undefined` sentinel on failure, and the caller's fallback (query all markets
 * on-chain) still holds.
 */
async function fetchJsonWithProxyFallback(
  url: string,
  body: string,
  proxyConfig: RelayProxyConfig | undefined,
): Promise<unknown | undefined> {
  const direct = await attemptFetchJson(url, body, undefined)
  if (direct !== undefined) return direct
  if (!proxyConfig) return undefined
  return attemptFetchJson(url, body, proxyConfig)
}

// ─── In-memory dedup cache ──────────────────────────────────────────────────
//
// Two layers keyed by `chainId:account`:
//  1. `inflight` — an unresolved Promise. Concurrent callers in the same
//     isolate share the upstream request; no cache staleness can result.
//  2. `resolved` — the previous resolution, valid for CACHE_TTL_MS. Purely
//     a rate-limit shield for rapid polling (e.g. sub-second UI loops).
//
// On Cloudflare Workers, an isolate's memory lives for seconds-to-minutes, so
// these maps naturally scope to a single worker instance. No cross-isolate
// sharing — that would need KV, which is both eventually consistent and has a
// 60s minimum TTL, both unacceptable here.

type CacheValue = MorphoUserMarketBalance[] | undefined

const inflight = new Map<string, Promise<CacheValue>>()
const resolved = new Map<string, { value: CacheValue; expiresAt: number }>()

function cacheKey(chainId: string, account: string): string {
  return `${chainId}:${account.toLowerCase()}`
}

async function dedupedFetch(
  chainId: string,
  account: string,
  loader: () => Promise<CacheValue>,
): Promise<CacheValue> {
  const key = cacheKey(chainId, account)
  const now = Date.now()

  const cached = resolved.get(key)
  if (cached && cached.expiresAt > now) {
    return cached.value
  }

  const existing = inflight.get(key)
  if (existing) return existing

  const promise = loader()
    .then((value) => {
      resolved.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS })
      return value
    })
    .finally(() => {
      inflight.delete(key)
    })

  inflight.set(key, promise)
  return promise
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function hasMorphoUserSubgraph(chainId: string): boolean {
  return chainId in MORPHO_USER_SUBGRAPH_URLS
}

export function hasMorphoUserApi(chainId: string): boolean {
  return chainId in MORPHO_API_CHAIN_IDS
}

export function hasMorphoPositionIndex(chainId: string): boolean {
  return hasMorphoUserSubgraph(chainId) || hasMorphoUserApi(chainId)
}

/**
 * Fetches per-market balance data (supply shares, borrow shares, collateral)
 * for all markets where the account has active positions. Queries the Goldsky
 * subgraph or the Morpho Blue API depending on the chain.
 *
 * Returns undefined when the chain has no index OR the request fails (timeout,
 * non-2xx, etc.) — caller should fall back to querying all markets on-chain.
 * Returns an empty array when the account has no positions on that chain.
 *
 * Concurrent callers for the same (chainId, account) share a single upstream
 * request, and results are memoized for a short window (see `CACHE_TTL_MS`).
 */
export async function fetchMorphoUserBalances(
  chainId: string,
  account: string,
  proxyConfig?: RelayProxyConfig,
): Promise<MorphoUserMarketBalance[] | undefined> {
  if (hasMorphoUserSubgraph(chainId)) {
    return dedupedFetch(chainId, account, () =>
      fetchBalancesFromSubgraph(chainId, account, proxyConfig),
    )
  }
  if (hasMorphoUserApi(chainId)) {
    return dedupedFetch(chainId, account, () =>
      fetchBalancesFromApi(chainId, account, proxyConfig),
    )
  }
  return undefined
}

/**
 * Convenience wrapper — returns only the set of market IDs where the account
 * has active positions. Use `fetchMorphoUserBalances` when you also need the
 * shares/collateral amounts.
 */
export async function fetchMorphoUserPositionMarkets(
  chainId: string,
  account: string,
  proxyConfig?: RelayProxyConfig,
): Promise<Set<string> | undefined> {
  const balances = await fetchMorphoUserBalances(chainId, account, proxyConfig)
  if (balances === undefined) return undefined
  return new Set(balances.map((b) => b.marketId))
}

// ─── Internal fetchers ────────────────────────────────────────────────────────

async function fetchBalancesFromSubgraph(
  chainId: string,
  account: string,
  proxyConfig?: RelayProxyConfig,
): Promise<MorphoUserMarketBalance[] | undefined> {
  const subgraphUrl = MORPHO_USER_SUBGRAPH_URLS[chainId]
  const json = (await fetchJsonWithProxyFallback(
    subgraphUrl,
    JSON.stringify({ query: buildSubgraphQuery(account) }),
    proxyConfig,
  )) as SubgraphAccountResponse | undefined
  if (!json) return undefined

  const positions = json.data?.account?.positions ?? []

  // Aggregate per-market across the three side entries
  const byMarket = new Map<
    string,
    { supplyShares: string; borrowShares: string; collateral: string }
  >()

  for (const pos of positions) {
    const marketId = pos.market.id.toLowerCase()
    if (!byMarket.has(marketId)) {
      byMarket.set(marketId, {
        supplyShares: '0',
        borrowShares: '0',
        collateral: '0',
      })
    }
    const entry = byMarket.get(marketId)!
    if (pos.side === 'SUPPLIER' && pos.shares) {
      entry.supplyShares = pos.shares
    } else if (pos.side === 'BORROWER' && pos.shares) {
      entry.borrowShares = pos.shares
    } else if (pos.side === 'COLLATERAL') {
      entry.collateral = pos.balance ?? '0'
    }
  }

  // Keep only markets with at least one non-zero value
  const result: MorphoUserMarketBalance[] = []
  for (const [marketId, entry] of byMarket) {
    if (
      isNonZero(entry.supplyShares) ||
      isNonZero(entry.borrowShares) ||
      isNonZero(entry.collateral)
    ) {
      result.push({ marketId, ...entry })
    }
  }
  return result
}

async function fetchBalancesFromApi(
  chainId: string,
  account: string,
  proxyConfig?: RelayProxyConfig,
): Promise<MorphoUserMarketBalance[] | undefined> {
  const numericChainId = MORPHO_API_CHAIN_IDS[chainId]
  const json = (await fetchJsonWithProxyFallback(
    MORPHO_BLUE_API_URL,
    JSON.stringify({ query: buildApiQuery(account, numericChainId) }),
    proxyConfig,
  )) as ApiUserResponse | undefined
  if (!json) return undefined

  const positions = json.data?.userByAddress?.marketPositions ?? []

  const result: MorphoUserMarketBalance[] = []
  for (const pos of positions) {
    const supplyShares = pos.state?.supplyShares ?? 0
    const borrowShares = pos.state?.borrowShares ?? 0
    const collateral = pos.state?.collateral ?? 0
    if (
      !isNonZero(supplyShares) &&
      !isNonZero(borrowShares) &&
      !isNonZero(collateral)
    ) {
      continue
    }
    result.push({
      marketId: pos.market.uniqueKey.toLowerCase(),
      supplyShares: String(supplyShares),
      borrowShares: String(borrowShares),
      collateral: String(collateral),
    })
  }
  return result
}

/**
 * Test-only helper: drops the in-memory cache so assertions don't interact
 * across tests. Safe to call at runtime, just wastes the next upstream call.
 */
export function __clearMorphoPositionCacheForTests(): void {
  inflight.clear()
  resolved.clear()
}
